import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import OptimizedImage from "@/components/ui/optimized-image";

interface Asset {
  id: string;
  name: string;
  location: string;
  status: "Live" | "Fund Raising" | "Acquired" | "Coming Soon";
  valuation: number;
  yield_avg: number;
  image_url: string | null;
}

const AssetsPreview = () => {
  const [assets, setAssets] = useState<Asset[]>([]);

  useEffect(() => {
    const fetchAssets = async () => {
      const { data } = await supabase
        .from("assets")
        .select("id, name, location, status, valuation, yield_avg, image_url")
        .order("valuation", { ascending: false })
        .limit(6);

      if (data) {
        setAssets(data as Asset[]);
      }
    };

    fetchAssets();
  }, []);

  const formatValuation = (value: number) => {
    if (value >= 1000000) {
      return `£${(value / 1000000).toFixed(1)}M`;
    }
    return `£${(value / 1000).toFixed(0)}K`;
  };

  const getStatusColor = (status: Asset["status"]) => {
    switch (status) {
      case "Live":
        return "bg-primary/10 text-primary border-primary/20";
      case "Fund Raising":
        return "bg-amber-500/10 text-amber-400 border-amber-500/20";
      case "Acquired":
        return "bg-blue-500/10 text-blue-400 border-blue-500/20";
      case "Coming Soon":
        return "bg-muted text-muted-foreground border-border";
      default:
        return "bg-muted text-muted-foreground border-border";
    }
  };

  // Return placeholder skeleton while loading to prevent layout shift
  // Using static opacity instead of animate-pulse to reduce CPU usage
  if (assets.length === 0) {
    return (
      <div className="w-full max-w-5xl mx-auto mt-12 pointer-events-none select-none" style={{ minHeight: '320px' }}>
        <div className="grid gap-3 sm:gap-4 grid-cols-2 sm:grid-cols-3 transform scale-[0.9] origin-top opacity-20">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="bg-card border border-border/50 overflow-hidden rounded-lg">
              <div className="bg-muted" style={{ aspectRatio: '400/250' }} />
              <div className="p-2 sm:p-3 space-y-2">
                <div className="h-3 bg-muted rounded w-16" />
                <div className="flex justify-between">
                  <div className="h-4 bg-muted rounded w-12" />
                  <div className="h-4 bg-muted rounded w-10" />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-5xl mx-auto mt-12 pointer-events-none select-none overflow-hidden" style={{ minHeight: '320px' }}>
      <div className="relative overflow-hidden">
        {/* Subtle glow effect behind cards - contained within parent */}
        <div className="absolute inset-0 bg-primary/5 blur-3xl rounded-full opacity-60" />
        
        {/* Fade overlay - stronger fade for bottom cards */}
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent via-40% to-background z-10" />
        
        {/* Assets grid - scaled down, 2 rows */}
        <div 
          className="grid gap-3 sm:gap-4 grid-cols-2 sm:grid-cols-3 transform scale-[0.9] origin-top"
          style={{ transform: "scale(0.9)" }}
        >
          {assets.map((asset, index) => (
            <div
              key={asset.id}
              className={`bg-card border border-border/50 overflow-hidden rounded-lg ${index >= 3 ? 'opacity-70' : 'opacity-80'}`}
            >
              {/* Image with name overlay */}
              <div className="relative">
                <OptimizedImage
                  src={asset.image_url}
                  alt={asset.name}
                  width={400}
                  height={250}
                  priority={index === 0}
                  sizes="(max-width: 640px) 45vw, (max-width: 1024px) 30vw, 280px"
                />
                {/* Name overlay at bottom of image */}
                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent p-2 sm:p-3">
                  <h3 className="font-medium text-[10px] sm:text-xs text-white leading-tight truncate">{asset.name}</h3>
                </div>
              </div>

              <div className="p-2 sm:p-3">
                {/* Status badge */}
                <div className="flex items-center justify-between mb-1.5 sm:mb-2">
                  <span className={`text-[6px] sm:text-[8px] font-medium px-1 sm:px-1.5 py-0.5 rounded-full border ${getStatusColor(asset.status)}`}>
                    {asset.status}
                  </span>
                </div>

                {/* Stats */}
                <div className="flex justify-between gap-2">
                  <div>
                    <p className="text-[7px] sm:text-[8px] text-foreground/60 mb-0.5">Valuation</p>
                    <p className="text-[10px] sm:text-xs font-semibold text-foreground">{formatValuation(asset.valuation)}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-[7px] sm:text-[8px] text-foreground/60 mb-0.5">Yield</p>
                    <p className="text-[10px] sm:text-xs font-semibold text-foreground">{asset.yield_avg}%</p>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AssetsPreview;
